import { useAppContext } from "@ftdr/blueprint-components-react";
import { useStripe } from "@stripe/react-stripe-js";
import { SetupIntentResult, StripeError } from "@stripe/stripe-js";
import { Dispatch, SetStateAction, useCallback } from "react";
import { usePaymentContext } from "../../../../../../contexts/payment-context";
import { usePaymentsClientContext } from "../../../../../../contexts/payments-client-context";
import {
  ProcessPaymentStripeRequestData,
  FailureTypeValue,
  OnResponseTypeValue,
  SuccessTypeValue,
  OnResponseType,
  ErrorType,
} from "../../../../types";

import { setError } from "../../../../../../contexts/payments-client-context/state";

export type UseRetrieveSetupIntentProps = {
  setError: Dispatch<SetStateAction<Partial<StripeError>>>;
};

export type RetrieveSetupIntentReturnType = {
  type?: OnResponseType;
  result?: SetupIntentResult;
  error?: ErrorType;
};

export type UseRetrieveSetupIntentReturnType = {
  retrieveSetupIntent: (
    clientSecret: string,
    requestData?: ProcessPaymentStripeRequestData
  ) => Promise<RetrieveSetupIntentReturnType>;
};

export const useRetrieveSetupIntent = (): UseRetrieveSetupIntentReturnType => {
  const { dispatch } = usePaymentsClientContext();
  const { onFailure, onResponse } = usePaymentContext();
  const {
    appSettings: { localizedText },
  } = useAppContext();
  const stripe = useStripe();

  const retrieveSetupIntent = useCallback(
    async (clientSecret: string): Promise<RetrieveSetupIntentReturnType> => {
      try {
        const setupIntentResult = await stripe?.retrieveSetupIntent(
          clientSecret
        );
        if (setupIntentResult?.error) {
          dispatch(setError({ message: setupIntentResult.error.message }));
          const errorResult = {
            type: FailureTypeValue.STRIPE_FAILURE,
            stripeFailureResponse: {
              message: setupIntentResult.error.message,
              rawError: setupIntentResult,
            },
          };
          onFailure(errorResult);
          return errorResult;
        } else {
          onResponse({
            type: OnResponseTypeValue.SETUP_CARD_SUCCESS,
            setupCardSuccessResponse: setupIntentResult,
          });
          return {
            type: SuccessTypeValue.SETUP_CARD_SUCCESS,
            result: setupIntentResult,
          };
        }
      } catch (ex) {
        dispatch(setError({ message: localizedText(`STRIPE_INTENT_ERROR`) }));
        const errorResult = {
          message: "STRIPE_INTENT_ERROR",
          rawError: ex as Record<string, unknown>,
        };
        onFailure({
          type: FailureTypeValue.STRIPE_FAILURE,
          stripeFailureResponse: errorResult,
        });

        return { type: FailureTypeValue.STRIPE_FAILURE, error: errorResult };
      }
    },
    [stripe, onFailure, onResponse, localizedText, dispatch]
  );
  return { retrieveSetupIntent };
};
